import React from 'react';
import {
	BaasicInput,
	Loader,
	BaasicCheckbox,
	BaasicHeader,
	BaasicButton,
	BaasicDropdown,
	TimePicker,
	DatePicker,
	SliderCheckBox,
	InfoPanel,
} from 'core/components';
import { defaultTemplate } from 'core/hoc';
import { channelType, channelTypesWithoutRequiredCarriers, getAmazonOAuthUrl } from 'common/constants';
import { observer } from 'mobx-react';
import { ButtonAndLink } from 'common/utils';
import ReactTooltip from 'react-tooltip';
import _ from 'lodash';

const ChannelEditTemplate = ({
	t,
	channelEditViewStore,
	channelEditViewStore: {
		form,
		loading,
		rootStore,
		isEdit,
		openEditRestockDateModal,
		openEditChannelWarehouseCodesModal,
		openExcludedCarrierServicesModal,
		allowedCarrierServiceCount,
		canEditExcludedCarrierServices,
		openUpdateInventoryLogModal,
		validateCredentials,
		scheduledRestockDate,
		openCustomLabelModal,
		openEditChannelAmazonTemplatesModal,
		channelTypeDropdownStore,
		trackingCarrierDropdowns,
		sellerDropdownStore,
		shipmentDocumentPullSourceDropdownStore,
		additionalDocumentPullSourceDropdownStore,
		addLogicBrokerExcludeOrderParameter,
		validateExcludeOrderParameters,
		showDuplicateExludeOrderParameterKeyError,
		showEmptyExcludeOrderParameterValueError,
		showEmptyExcludeOrderParameterKeyError,
	},
}) => {
	if (loading) return <Loader />;
	const pageTitle = !isEdit ? 'CREATECHANNEL' : 'EDITCHANNEL';
	const isShipStationChannel = _.get(channelTypeDropdownStore, 'value.label') === channelType.shipStation;

	return (
		<section>
			<BaasicHeader title={pageTitle} rootStore={rootStore}>
				{scheduledRestockDate && (
					<i
						data-tip={t('RESTOCKDATE.SCHEDULEDRESTOCKDATENOTE', { scheduledDate: scheduledRestockDate })}
						className="spc--right--sml far fa-clock type--color--success--light type--med"
					/>
				)}
				<ReactTooltip />
				{/* Restock Date */}
				{isEdit && _.get(channelTypeDropdownStore, 'value.label') === channelType.amazon && (
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative"
						icon="icon icon--xmed icon--inventory-update spc--right--sml"
						onClick={() => {
							openEditRestockDateModal();
						}}
						label="RESTOCKDATE.TITLE"
					/>
				)}
				{/* Update Inventory */}
				{isEdit && _.get(channelTypeDropdownStore, 'value.label') !== channelType.manualOrders && (
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
						icon="icon icon--xmed icon--inventory-update spc--right--sml"
						onClick={() => {
							openUpdateInventoryLogModal();
						}}
						label="UPDATEINVENTORYLOG.TITLE"
					/>
				)}
				{/* Warehouse Codes */}
				<BaasicButton
					className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
					icon="icon icon--xmed icon--warehouse-code--white spc--right--sml"
					onClick={() => {
						openEditChannelWarehouseCodesModal();
					}}
					label="WAREHOUSECODES.EDIT"
				/>
				{/* Excluded Carrier Services */}
				<BaasicButton
					className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
					onClick={() => {
						openExcludedCarrierServicesModal();
					}}
					disabled={!canEditExcludedCarrierServices}
					title={
						canEditExcludedCarrierServices
							? 'EXCLUDEDCARRIERSERVICES.EDIT'
							: 'EXCLUDEDCARRIERSERVICES.NOREQUIREDCARRIERS'
					}
					content={
						<React.Fragment>
							<span className="fas fa-truck spc--right--sml" />
							{t('EXCLUDEDCARRIERSERVICES.EDIT')}
							<span className="spc--left--tny type--wgt--bold">{`(${allowedCarrierServiceCount})`}</span>
						</React.Fragment>
					}
				/>
				{/* Amazon Templates */}
				{_.get(channelTypeDropdownStore, 'value.label') == channelType.amazon && (
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
						icon="icon icon--xmed icon--amazon--white spc--right--sml"
						onClick={() => {
							openEditChannelAmazonTemplatesModal();
						}}
						label="AMAZONTEMPLATES.EDIT"
					/>
				)}

				{/* Test Connection */}
				{_.get(channelTypeDropdownStore, 'value.label') !== channelType.manualOrders && (
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
						icon="icon icon--xmed icon--test-connection--white spc--right--sml"
						onClick={() => {
							validateCredentials();
						}}
						label="CHANNEL.TESTCONNECTION"
					/>
				)}
				{
					<BaasicButton
						className="btn btn--med btn--primary btn--primary--negative spc--left--tny"
						icon="icon icon--xmed icon--custom-label--white spc--right--sml"
						onClick={() => {
							openCustomLabelModal();
						}}
						label="CHANNEL.CUSTOMIZELABEL"
					/>
				}
			</BaasicHeader>
			<form className="container container--lrg container--footer" onSubmit={form.onSubmit}>
				<div className="container__group__wrapper">
					<GeneralChannelSettings viewStore={channelEditViewStore} t={t} />
					{<ChannelSettings viewStore={channelEditViewStore} t={t} />}

					{[channelType.goFlow, channelType.manualOrders, channelType.sellerCloud].includes(_.get(channelTypeDropdownStore, 'value.label')) && (
						<div className="container__group spc--top--sml">
							<BaasicCheckbox field={form.$('useAmazonBuyShipping')} />
							<div className="spc--top--sml">
								{form.$('useAmazonBuyShipping').value === true && (
									<AmazonChannelSettings viewStore={channelEditViewStore} t={t} useAmazonBuyShipping={true} />
								)}
							</div>
						</div>
					)}

					<div className="container__group spc--top--sml">
						{_.get(sellerDropdownStore, 'value') && !isShipStationChannel && (
							<div className="container__group">
								<h3 className="container__subtitle">{'Tracking Carriers'}</h3>
								{trackingCarrierDropdowns.map(item => {
									return (
										<React.Fragment id={item.id}>
											<div className="row">
												<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
													<BaasicDropdown dropdownStore={item} isClearable={true} />
												</div>
											</div>
										</React.Fragment>
									);
								})}
							</div>
						)}
					</div>

					{shipmentDocumentPullSourceDropdownStore && (
						<React.Fragment>
							<div className="container__group spc--top--sml">
								<div className="row">
									<div className="col col-sml-12 col-lrg-6">
										<BaasicDropdown dropdownStore={shipmentDocumentPullSourceDropdownStore} isClearable={true} />
									</div>
									{_.get(channelTypeDropdownStore, 'value.label') === channelType.sellerCloud && (
										<div className="col col-sml-12 col-lrg-6">
											<BaasicDropdown dropdownStore={additionalDocumentPullSourceDropdownStore} isClearable={true} />
										</div>
									)}
									{_.get(channelTypeDropdownStore, 'value.label') === channelType.sellerCloud && (
										<div className="col col-sml-12 col-lrg-12">
											<div className="spc--top--sml spc--bottom--sml">
												<BaasicCheckbox field={form.$('useCustomShipmentTrackingColumn')} />
											</div>
										</div>
									)}
									{_.get(shipmentDocumentPullSourceDropdownStore, 'value.value') === 1 && (
										<div className="col col-sml-12 col-lrg-12">
											<div className="spc--top--sml">
												<BanyanCredentials viewStore={channelEditViewStore} />
											</div>
										</div>
									)}
								</div>
							</div>
						</React.Fragment>
					)}

					{_.get(channelTypeDropdownStore, 'value.label') &&
						[channelType.logicBroker].includes(_.get(channelTypeDropdownStore, 'value.label')) && (
							<React.Fragment>
								<div className="container__group spc--top--sml">
									<h3 className="container__subtitle">{'Exclude Order Parameters'}</h3>

									<div className="col col-sml-12 col-lrg-8">
										<InfoPanel
											messages={[
												'Orders containing these extended attributes will not be imported from LogicBroker.',
												'Example: IsCastleGate = true',
											]}
										/>
									</div>

									<div className="spc--top--med"></div>

									{form.$('logicBrokerExcludeOrderParameters').map(logicBrokerParam => (
										<div className="row" key={logicBrokerParam.key}>
											<div className="col col-sml-12 col-lrg-4">
												<label className="spc--left--tny"> Key <span className='type--color--warning'>*</span> </label>
											</div>
											<div className="col col-sml-12 col-lrg-4">
												<label className="spc--left--tny"> Value <span className='type--color--warning'>*</span> </label>
											</div>
											<div className="col col-sml-12 col-lrg-4"></div>

											<div className="col col-sml-12 col-lrg-4">
												<BaasicInput
													field={logicBrokerParam.$('key')}
													placeholder="..."
													onKeyUp={validateExcludeOrderParameters}
													onBlur={validateExcludeOrderParameters}
												/>
											</div>
											<div className="col col-sml-12 col-lrg-4">
												<BaasicInput
													field={logicBrokerParam.$('value')}
													placeholder="..."
													onKeyUp={validateExcludeOrderParameters}
													onBlur={validateExcludeOrderParameters}
												/>
											</div>
											<div className="col col-sml-12 col-lrg-4">
												<BaasicButton
													type="button"
													onClick={() => {
														form.$('logicBrokerExcludeOrderParameters').del(logicBrokerParam.key);
														validateExcludeOrderParameters();
													}}
													icon="fa fa-trash type--color--warning"
												/>
											</div>
										</div>
									))}

									{showDuplicateExludeOrderParameterKeyError && (
										<div className="type--tny type--color--error spc--bottom--sml">
											{t('CHANNEL.DUPLICATEKEYSARENOTALLOWED')}
										</div>
									)}

									<BaasicButton
										className="btn btn--med btn--secondary spc--bottom--sml"
										type="button"
										onClick={addLogicBrokerExcludeOrderParameter}
										icon="fa fa-plus spc--right--sml"
										label="Add Exclude Order Parameter"
									/>
								</div>
							</React.Fragment>
						)}

					<div className="container__group spc--top--sml">
						<div className="row">
							<div className="col col-sml-12 col-lrg-6">
								{_.get(channelTypeDropdownStore, 'value.label') &&
									![channelType.goFlow, channelType.sellerCloud, channelType.manualOrders].includes(
										_.get(channelTypeDropdownStore, 'value.label')
									) && (
										<div className="spc--bottom--sml">
											<BaasicCheckbox field={form.$('shouldAcknowledgeOrders')} />
										</div>
									)}

								{(_.get(channelTypeDropdownStore, 'value.label') === channelType.amazon ||
									_.get(channelTypeDropdownStore, 'value.label') === channelType.walmart) && (
									<div className="spc--bottom--sml">
										<BaasicCheckbox field={form.$('shouldAutoCancel')} />
									</div>
								)}

								<div className="spc--bottom--sml">
									<BaasicCheckbox
										field={form.$('doNotShipAsap')}
										additionalLabel={() => (
											<i data-tip={t('CHANNEL.DONOTSHIPASAPINFO')} className="fas fa-exclamation-circle"></i>
										)}
									/>
								</div>

								<div className="spc--bottom--sml">
									<BaasicCheckbox
										field={form.$('delayLabelPull')}
										additionalLabel={() => (
											<i data-tip={t('CHANNEL.DELAYLABELPULLINFO')} className="fas fa-exclamation-circle"></i>
										)}
									/>
								</div>

								{_.get(channelTypeDropdownStore, 'value.label') === channelType.manualOrders && (
									<div className="spc--bottom--sml">
										<BaasicCheckbox
											field={form.$('raiseUnlinkedSkuWarning')}
											additionalLabel={() => (
												<i
													data-tip={t('CHANNEL.RAISEUNLINKEDSKUWARNINGINFO')}
													className="fas fa-exclamation-circle"
												></i>
											)}
										/>
									</div>
								)}

								{_.get(channelTypeDropdownStore, 'value.label') === channelType.amazon && (
									<div className="spc--bottom--sml">
										<BaasicCheckbox field={form.$('checkBuyShippingSunday')} />
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className="container__footer type--right">
					<button className="btn btn--med btn--success spc--bottom--sml" type="submit" disabled={loading}>
						{loading ? (
							<div className="spinnercont spc--right--sml">
								<div className="spinner"></div>
							</div>
						) : null}
						{t('SAVE')}
					</button>
				</div>
			</form>
		</section>
	);
};

const GoFlowChannelSettings = observer(({ viewStore, t }) => {
	const { form, goFlowStoreDropdownStore, getGoFlowStores } = viewStore;
	return (
		<React.Fragment>
			<div className="container__group">
				<div className="row">
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('apiKey')} />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('subdomain')} />
					</div>
				</div>
				{goFlowStoreDropdownStore && (
					<div className="row row--form">
						<div className="col col-sml-12 col-lrg-6">
							<BaasicDropdown dropdownStore={goFlowStoreDropdownStore} />
						</div>
						<div className="col col-sml-12 col-lrg-6">
							<BaasicButton
								className="btn btn--med btn--secondary"
								label={t('CHANNEL.GETSTORES')}
								onClick={getGoFlowStores}
							/>
						</div>
					</div>
				)}
			</div>
			<div className="container__group">
				<BaasicCheckbox field={form.$('checkSplit')} />
			</div>
		</React.Fragment>
	);
});

const SellerCloudChannelSettings = observer(({ viewStore, t }) => {
	const {
		form,
		sellerCloudDropdownStore,
		sellerCloudCompanyDropdownStore,
		getSellerCloudCompanies,
		sellerCloudSavedViewDropdownStore,
		getSellerCloudSavedViews,
		onFilterOrdersToPullChange,
	} = viewStore;
	return (
		<React.Fragment>
			<div className="container__group">
				<div className="row">
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('teamName')} />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('userName')} />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('password')} />
					</div>
					{!form.$('filterOrdersToPull').value && (
						<div className="col col-sml-12 col-lrg-6">
							<BaasicDropdown dropdownStore={sellerCloudDropdownStore} />
						</div>
					)}
				</div>
				{sellerCloudCompanyDropdownStore && !form.$('filterOrdersToPull').value && (
					<div className="row row--form">
						<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
							<BaasicDropdown dropdownStore={sellerCloudCompanyDropdownStore} />
						</div>
						<div className="col col-sml-12 col-lrg-6">
							<BaasicButton
								className="btn btn--med btn--secondary"
								label={t('CHANNEL.GETCOMPANIES')}
								onClick={getSellerCloudCompanies}
							/>
						</div>
					</div>
				)}
				{sellerCloudSavedViewDropdownStore && form.$('filterOrdersToPull').value && (
					<div className="row row--form">
						<div className="col col-sml-12 col-lrg-6">
							<BaasicDropdown dropdownStore={sellerCloudSavedViewDropdownStore} />
						</div>
						<div className="col col-sml-12 col-lrg-6">
							<BaasicButton
								className="btn btn--med btn--secondary"
								label={t('CHANNEL.GETSAVEDVIEWS')}
								onClick={getSellerCloudSavedViews}
							/>
						</div>
					</div>
				)}
			</div>
			<div className="container__group">
				<div className="spc--bottom--sml">
					<BaasicCheckbox field={form.$('filterOrdersToPull')} onChangeCallback={onFilterOrdersToPullChange} />
				</div>
				<div className="spc--bottom--sml">
					<BaasicCheckbox field={form.$('flagOrderAsImported')} />
				</div>
				<div className="spc--bottom--sml">
					<BaasicCheckbox field={form.$('flagOrderAsShipped')} />
				</div>
			</div>
		</React.Fragment>
	);
});

const BanyanCredentials = ({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('banyanClientId')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('banyanClientSecret')} />
				</div>
			</div>
		</div>
	);
};

const MiraklChannelSettings = ({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('url')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('shopId')} />
				</div>
			</div>
		</div>
	);
};

const GeneralChannelSettings = observer(({ viewStore, t }) => {
	const {
		form,
		sellerDropdownStore,
		isSuperAdmin,
		isEdit,
		channelTypeDropdownStore,
		timezoneDropdownStore,
		carrierChannelDropdownStore,
		datePickerFormat,
		onLastSyncChange,
	} = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
					{isSuperAdmin && (
						<BaasicDropdown
							dropdownStore={sellerDropdownStore}
							placeholder={t('PLACEHOLDER.SELLER')}
							className="w--200--px spc--right--sml"
							isDisabled={isEdit}
						/>
					)}
				</div>
			</div>
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput autoFocus field={form.$('name')} />
				</div>
				<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
					<BaasicDropdown dropdownStore={channelTypeDropdownStore} isDisabled={isEdit} />
				</div>
				<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
					<BaasicDropdown dropdownStore={timezoneDropdownStore} />
				</div>
				{channelTypeDropdownStore.value &&
					!channelTypesWithoutRequiredCarriers.includes(channelTypeDropdownStore.value.label) && (
						<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
							<BaasicDropdown dropdownStore={carrierChannelDropdownStore} />
						</div>
					)}
				{!form.$('filterOrdersToPull').value &&
					isEdit &&
					isSuperAdmin &&
					_.get(channelTypeDropdownStore, 'value.label') !== channelType.manualOrders && (
						<React.Fragment>
							<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
								<DatePicker field={form.$('lastSyncDate')} format={datePickerFormat} onChange={onLastSyncChange} />
							</div>
							<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
								<TimePicker field={form.$('lastSyncTime')} />
							</div>
						</React.Fragment>
					)}
			</div>
		</div>
	);
});

const ShopifyChannelSettings = observer(({ viewStore, t }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			{form.$('accessToken').value && (
				<div className="row spc--bottom--med">
					<div className="col col-sml-12 col-lrg-6">
						<InfoPanel
							messages={[
								'We now support Client ID and Secret authorization.',
								'Direct access tokens are now legacy, but your current connection remains active with no action required.',
							]}
						/>
					</div>
				</div>
			)}
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('url')} className="fs-mask" />
				</div>
				{form.$('accessToken').value && (
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('accessToken')} className="fs-mask" />
					</div>
				)}
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('clientId')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('clientSecret')} className="fs-mask" />
				</div>
				{/* <div className="col col-sml-12 col-lrg-6">
					<BaasicButton
						className="btn btn--med btn--secondary"
						label={t('CHANNEL.AUTHORIZEVENDOR')}
						onClick={() => onAuthorizeShopify()}
					/>
				</div> */}
			</div>
		</div>
	);
});

const OverstockCAChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('partnerId')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const LogicbrokerChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('partnerId')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const CostcoChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('partnerId')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const WooCommerceChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('url')} />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('consumerKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('consumerSecret')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const AmazonVendorChannelSettings = observer(({ viewStore, t }) => {
	const { form, onAuthorizeAmazon, amazonOAuthStateHash } = viewStore;
	return (
		<React.Fragment>
			<div className="container__group">
				<h3 className="container__subtitle">{t('CHANNEL.SELLINGPARTNERAPICREDENTIALS')}</h3>
				<div className="row">
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('amazonSellerId')} className="fs-mask" />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('marketplaceId')} />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('refreshToken')} className="fs-mask" />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('sellingPartyId')} className="fs-mask" />
					</div>
				</div>
				<ButtonAndLink
					label={t('CHANNEL.AUTHORIZEVENDOR')}
					btnClass="btn btn--med btn--primary spc--right--sml spc--bottom--sml"
					url={getAmazonOAuthUrl(true, amazonOAuthStateHash)}
					onClick={() => onAuthorizeAmazon()}
				/>
			</div>

			{process.env.NODE_ENV !== 'production' && (
				<div className="spc--bottom--sml">
					<BaasicCheckbox field={form.$('useSandbox')} />
				</div>
			)}
		</React.Fragment>
	);
});

const AmazonChannelSettings = observer(({ viewStore, t, useAmazonBuyShipping = false }) => {
	const { form, onAuthorizeAmazon, amazonOAuthStateHash, isEdit, channel, buyShippingDropdowns } = viewStore;
	return (
		<React.Fragment>
			<div className="container__group">
				{!useAmazonBuyShipping && <h3 className="container__subtitle">{t('CHANNEL.SELLINGPARTNERAPICREDENTIALS')}</h3>}
				<div className="row">
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('amazonSellerId')} className="fs-mask" />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('marketplaceId')} />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('refreshToken')} className="fs-mask" />
					</div>
				</div>

				{
					<ButtonAndLink
						label={t('CHANNEL.AUTHORIZESELLER')}
						btnClass="btn btn--med btn--primary spc--right--sml spc--bottom--med"
						url={getAmazonOAuthUrl(false, amazonOAuthStateHash)}
						onClick={() => onAuthorizeAmazon()}
					/>
				}
				<h3 className="container__subtitle">{t('CHANNEL.BUYSHIPPINGCARRIERS')}</h3>

				{/* Use Buy Shipping toogle/slider */}
				<div className="spc--bottom--sml">
					<SliderCheckBox field={form.$('useBuyShippingForPrime')} />
				</div>
				<div className="spc--bottom--sml">
					<SliderCheckBox field={form.$('useBuyShippingForNonPrime')} />
				</div>
				<div className="spc--bottom--sml">
					<SliderCheckBox field={form.$('useShippingApiV2')} />
				</div>

				{buyShippingDropdowns.map(item => {
					return (
						<React.Fragment id={item.id}>
							<div className="row">
								<div className="col col-sml-12 col-lrg-6 spc--bottom--sml">
									<BaasicDropdown
										dropdownStore={item}
										isDisabled={!form.$('useBuyShippingForPrime').value && !form.$('useBuyShippingForNonPrime').value}
										isClearable={true}
									/>
								</div>
							</div>
						</React.Fragment>
					);
				})}

				<h3 className="container__subtitle">{t('CHANNEL.UPDATEINVENTORY')}</h3>
				{isEdit && form.$('useAmazonMli').value == false && form.$('useAmazonMli').value != channel.useAmazonMli && (
					<p className="type--color--note spc--bottom--sml">{t('CHANNEL.MLIRESET')}</p>
				)}
				<div className="spc--bottom--sml">
					<SliderCheckBox field={form.$('useAmazonMli')} />
				</div>
			</div>
		</React.Fragment>
	);
});

const WalmartChannelSettings = observer(({ viewStore, t }) => {
	const {
		form,
		isEdit,
		isWalmartOAuthConnected,
		walmartAuthorizedOnDisplay,
		walmartNeedsReauthorization,
		onAuthorizeWalmart,
		onDisconnectWalmart,
	} = viewStore;
	return (
		<React.Fragment>
			<div className="container__group">
				<h3 className="container__subtitle">{t('CHANNEL.WALMARTCONNECTION')}</h3>
				{!isEdit ? (
					<div className="col col-sml-12 col-lrg-8">
						<InfoPanel messages={[t('CHANNEL.WALMARTCONNECTAFTERCREATE')]} />
					</div>
				) : (
					<React.Fragment>
						{isWalmartOAuthConnected ? (
							<div className="spc--bottom--sml">
								<p>
									<i className="type--color--success fas fa-check-circle spc--right--tny" />
									{t('CHANNEL.WALMARTCONNECTEDTO', { sellerId: form.$('walmartSellerId').value })}
								</p>
								{walmartAuthorizedOnDisplay && (
									<p className="type--color--grey-5">
										{t('CHANNEL.WALMARTAUTHORIZEDONVALUE', { date: walmartAuthorizedOnDisplay })}
									</p>
								)}
								{walmartNeedsReauthorization && (
									<p className="type--color--warning">{t('CHANNEL.WALMARTNEEDSREAUTHORIZATION')}</p>
								)}
							</div>
						) : (
							<p className="spc--bottom--sml">
								<i className="type--color--grey-5 fas fa-times-circle spc--right--tny" />
								{t('CHANNEL.WALMARTNOTCONNECTED')}
							</p>
						)}
						<button
							type="button"
							className="btn btn--med btn--primary spc--right--sml spc--bottom--sml"
							onClick={onAuthorizeWalmart}
						>
							{isWalmartOAuthConnected ? t('CHANNEL.WALMARTREAUTHORIZE') : t('CHANNEL.WALMARTCONNECT')}
						</button>
						{isWalmartOAuthConnected && (
							<button
								type="button"
								className="btn btn--med btn--secondary spc--right--sml spc--bottom--sml"
								onClick={onDisconnectWalmart}
							>
								{t('CHANNEL.WALMARTDISCONNECT')}
							</button>
						)}
					</React.Fragment>
				)}
			</div>

			<div className="container__group">
				<h3 className="container__subtitle">{t('CHANNEL.WALMARTSANDBOX')}</h3>
				<BaasicCheckbox field={form.$('walmartUseSandbox')} />
				{form.$('walmartUseSandbox').value && (
					<div className="row spc--top--sml">
						<div className="col col-sml-12 col-lrg-6">
							<BaasicInput field={form.$('walmartSellerId')} className="fs-mask" />
						</div>
						<div className="col col-sml-12 col-lrg-6">
							<BaasicInput field={form.$('refreshToken')} className="fs-mask" />
						</div>
					</div>
				)}
			</div>

			<div className="container__group">
				<h3 className="container__subtitle">{t('CHANNEL.WALMARTLEGACYCREDENTIALS')}</h3>
				<div className="row">
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('apiKey')} className="fs-mask" />
					</div>
					<div className="col col-sml-12 col-lrg-6">
						<BaasicInput field={form.$('apiSecret')} className="fs-mask" />
					</div>
				</div>
			</div>
		</React.Fragment>
	);
});

const ShipStationChannelSettings = observer(({ viewStore }) => {
	const { form } = viewStore;
	return (
		<div className="container__group">
			<div className="row">
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKey')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiSecret')} className="fs-mask" />
				</div>
				<div className="col col-sml-12 col-lrg-6">
					<BaasicInput field={form.$('apiKeyV2')} className="fs-mask" />
				</div>
			</div>
		</div>
	);
});

const ChannelSettings = observer(({ viewStore, t }) => {
	switch (_.get(viewStore.channelTypeDropdownStore, 'value.label')) {
		case channelType.goFlow:
			return <GoFlowChannelSettings viewStore={viewStore} t={t} />;
		case channelType.amazonVendor:
			return <AmazonVendorChannelSettings viewStore={viewStore} t={t} />;
		case channelType.amazon:
			return <AmazonChannelSettings viewStore={viewStore} t={t} />;
		case channelType.walmart:
			return <WalmartChannelSettings viewStore={viewStore} t={t} />;
		case channelType.wooCommerce:
			return <WooCommerceChannelSettings viewStore={viewStore} />;
		case channelType.costco:
			return <CostcoChannelSettings viewStore={viewStore} />;
		case channelType.logicBroker:
			return <LogicbrokerChannelSettings viewStore={viewStore} />;
		case channelType.overstockCA:
			return <OverstockCAChannelSettings viewStore={viewStore} />;
		case channelType.buybuyBaby:
		case channelType.mattressFirm:
		case channelType.homeDepot:
		case channelType.overstock:
		case channelType.wayfair:
			return <LogicbrokerChannelSettings viewStore={viewStore} />;
		case channelType.mirakl:
			return <MiraklChannelSettings viewStore={viewStore} />;
		case channelType.shopify:
			return <ShopifyChannelSettings viewStore={viewStore} t={t} />;
		case channelType.sellerCloud:
			return <SellerCloudChannelSettings viewStore={viewStore} t={t} />;
		case channelType.shipStation:
			return <ShipStationChannelSettings viewStore={viewStore} />;
	}
});

export default defaultTemplate(ChannelEditTemplate);
